import { useEffect, useMemo, useState } from 'react';
import { Api } from '../../services/api';
import { redirectToLogin } from '../../services/auth';
import AddAddressModal from '../checkout/AddAddressModal';
import { useLanguage } from '../../context/LanguageContext';
import editIcon from '../../assets/edit-2.png';
import trashIcon from '../../assets/Trash_light.png';

const normalizeAddress = (entry) => ({
  ...entry,
  id: entry.addressId || entry.id || entry._id,
  summary: [entry.street, entry.building, entry.apartment, entry.city]
    .filter(Boolean)
    .join(', '),
  defaultAddress:
    entry.defaultAddress ||
    entry.isDefault ||
    entry.default ||
    entry.is_default ||
    entry.selected,
});

const AddressesPage = () => {
  const [customerId, setCustomerId] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const { t } = useLanguage();

  /* Load user */
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const meta = JSON.parse(window.localStorage.getItem('myUser') || '{}');
      if (!meta?.customer_id) {
        redirectToLogin();
        return;
      }
      setCustomerId(meta.customer_id);
    } catch {
      redirectToLogin();
    }
  }, []);

  /* Fetch addresses */
  const fetchAddresses = useMemo(
    () => async () => {
      if (!customerId) return;
      setLoading(true);
      setError(null);
      try {
        const response = await Api.fetchCustomerAddresses(customerId);
        const payload = response?.data?.data || response?.data || [];
        setAddresses(Array.isArray(payload) ? payload.map(normalizeAddress) : []);
      } catch (err) {
        setAddresses([]);
        setError(err?.message || t('header.addresses.load_error'));
      } finally {
        setLoading(false);
      }
    },
    [customerId, t]
  );

  useEffect(() => {
    fetchAddresses();
  }, [fetchAddresses]);

  const handleDelete = async (address) => {
    const id = address?.addressId || address?.id;
    if (!id || typeof window === 'undefined') return;

    if (!window.confirm(t('addresses.delete_confirm'))) return;

    try {
      await Api.deleteAddress(id);
      fetchAddresses();
    } catch (err) {
      window.alert(err?.message || t('addresses.delete_error'));
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingAddress(null);
  };

  const handleAddressSaved = () => {
    handleModalClose();
    fetchAddresses();
  };

  const handleAddClick = () => {
    setEditingAddress(null);
    setModalOpen(true);
  };

  const handleEditClick = (address) => {
    setEditingAddress(address);
    setModalOpen(true);
  };

  const handleSetDefault = async (address) => {
    const id = address?.addressId || address?.id;
    if (!id || !customerId || address?.defaultAddress) return;
    try {
      await Api.setDefaultAddress(id, customerId);
      setAddresses((prev) =>
        prev.map((item) => {
          const itemId = item?.addressId || item?.id;
          return {
            ...item,
            defaultAddress: itemId === id,
          };
        })
      );
    } catch (error) {
      window.alert(error?.message || t('addresses.default_error'));
    }
  };

  return (
    <div className="delivery-address-page">

      {/* Title */}
      <h2 className="delivery-title">{t('addresses.select')}</h2>

      {/* Status */}
      {loading ? (
        <p className="delivery-status">{t('addresses.loading')}</p>
      ) : error ? (
        <p className="delivery-status error">{error}</p>
      ) : !addresses.length ? (
        <p className="delivery-status">{t('addresses.empty')}</p>
      ) : (
        <div className="delivery-address-list">
          {addresses.map((address) => (
            <div
              key={address.id}
              className={`delivery-card ${
                address.defaultAddress ? 'active' : ''
              }`}
            >
              <button
                type="button"
                className="delivery-card-body"
                onClick={() => handleSetDefault(address)}
              >
                <input
                  type="radio"
                  className="delivery-radio"
                  checked={address.defaultAddress}
                  readOnly
                />
                <div>
                  <div className="delivery-name">
                    {address.name}
                    {address.defaultAddress ? (
                      <span className="delivery-default-badge">
                        {t('addresses.default')}
                      </span>
                    ) : null}
                  </div>
                  <div className="delivery-summary">
                    {address.summary || address.city || '-'}
                  </div>
                </div>
              </button>

              <div className="delivery-card-actions">
                <button
                  className="icon-btn-address"
                  onClick={() => handleEditClick(address)}
                  aria-label={t('addresses.edit')}
                >
                  <img src={editIcon} alt="" />
                </button>
                <button
                  className="icon-btn-address"
                  onClick={() => handleDelete(address)}
                  aria-label={t('addresses.delete')}
                >
                  <img src={trashIcon} alt="" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add address button */}
      <div className="delivery-add-wrapper">
        <button className="delivery-add-btn" onClick={handleAddClick}>
          {t('addresses.add')}
        </button>
      </div>

      {/* Modal */}
      {modalOpen && (
        <AddAddressModal
          open={modalOpen}
          onClose={handleModalClose}
          customerId={customerId}
          address={editingAddress}
          onCreated={handleAddressSaved}
          onUpdated={handleAddressSaved}
        />
      )}
    </div>
  );
};

export default AddressesPage;
